import { Purchase } from '@com-delta-idp/mnx-sky-club-ui';
import {
  AEMErrorMessage,
  checkErrors,
  getEmptyContent,
  isDisplay,
  isError,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { logError } from '@com-delta-idp/mnx-utils';
import { Option } from '@com-delta-omnichannel/aura';
import {
  AEMPurchaseContent,
  CURRENCY,
  MembershipDetailMap,
  MembershipDetails,
  NewMembershipOptions,
  Options,
  SKY_CLUB_ERROR
} from '../models';
import { createCurrencyOption, createMilesOption } from './purchase.adapter-helper';

export const getPurchase = (
  aem: AEMPurchaseContent | undefined,
  membership: MembershipDetailMap | undefined,
  aemErrorMessages: AEMErrorMessage[] | undefined,
  errors: (string | undefined)[],
  featureFlag: boolean | undefined
): Purchase => {
  try {
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<Purchase>(
        errors.find((error) => error != undefined) as string,
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return isValidData<
      AEMPurchaseContent,
      [AEMPurchaseContent | undefined, boolean | undefined]
    >('total', aem, featureFlag)
      ? adaptPurchase(
        aem as AEMPurchaseContent,
        membership,
        featureFlag as boolean
      )
      : isDisplay<Purchase>(featureFlag);
  } catch (error) {
    logError(
      error as Error,
      'DIGITAL_CUSTOMER_LOYALTY',
      'SKY_CLUB',
      'PURCHASE'
    );
    return getEmptyContent<Purchase>(
      SKY_CLUB_ERROR,
      aemErrorMessages,
      true,
      true
    );
  }
};

export const adaptPurchase = (
  aem: AEMPurchaseContent,
  membership: MembershipDetailMap | undefined,
  featureFlag: boolean
): Purchase => {
  return featureFlag
    ? {
      options: getOptionsData(aem.options, membership),
      action: aem.action,
      total: aem.total,
      disabled: !featureFlag,
      isMembership: !!membership,
      loaded: true
    }
    : getEmptyContent<Purchase>('', [], true, true);
};

export const getOptionsData = (
  aemContent: Options[],
  membershipDetail: MembershipDetailMap | undefined
): Options[] => {
  let membershipOptions: Options[] = [];
  let newOrRenewalmembershipOptions: NewMembershipOptions = {};
  if (membershipDetail) {
    newOrRenewalmembershipOptions = membershipDetail['NEW MEMBERSHIP OPTIONS']  || membershipDetail['MEMBERSHIP RENEWAL OPTIONS'] || {};
    membershipOptions = processMembershipOptions(aemContent, newOrRenewalmembershipOptions);
  }
  return membershipOptions;
};

export const processMembershipOptions = (
  aemContent: Options[],
  membershipTypeOptions: NewMembershipOptions
): Options[] => {
  return Object.keys(membershipTypeOptions).map(key => 
    createOptionForMembershipType(key, membershipTypeOptions, aemContent)
  );
};

const createOptionForMembershipType = (
  key: string, 
  membershipTypeOptions: NewMembershipOptions,
  aemContent: Options[]
): Options => {
  const membershipDetailsArray = membershipTypeOptions[key as keyof NewMembershipOptions];
  if (!membershipDetailsArray) {
    return {} as Options;
  }
  const currencies = createCurrencyOptions(membershipDetailsArray, aemContent);
  return {
    type: {
      title: key,
      subtitle: aemContent[0].type.subtitle,
      content: membershipDetailsArray[0]?.expirationDate
    },
    currencies,
    membershipDetails: membershipDetailsArray as MembershipDetails[]
  };
};

const createCurrencyOptions = (
  membershipDetails: MembershipDetails[],
  aemContent: Options[]
): Option[] => {
  return membershipDetails.map(element => {
    if (element.denomination === CURRENCY) {
      return createCurrencyOption(
        element.shoppingValue,
        element.currencyCode,
        aemContent[0].currencies[0].label
      );
    }
    return createMilesOption(
      element.shoppingValue,
      aemContent[0].currencies[1].label
    );
  });
};
